import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Chip,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BombIcon from '@mui/icons-material/NewReleases';

const TimeProgressBar = ({ 
  createdAt, 
  defectTypeSLA, 
  status, 
  defectType,
  validationStatus,
  resolvedAt = null,           // ✅ New: Timestamp when defect was resolved
  workOrderStatus = null,      // ✅ New: Current work order status
  hasWorkOrder = false         // ✅ New: Whether defect has work orders
}) => {
  const theme = useTheme();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [elapsedDisplay, setElapsedDisplay] = useState({
    text: '',
    hours: 0,
    minutes: 0,
    seconds: 0,
    percentage: 0
  });

  // Check if this defect type has SLA settings
  const hasSLA = defectTypeSLA && (defectTypeSLA.temp_fix_sla_hours || defectTypeSLA.permanent_fix_sla_hours);
  
  // Only show progress for validated findings with SLA settings
  const shouldShowProgress = hasSLA && validationStatus === 'validated';
  
  // ✅ Determine if SLA should be stopped (defect resolved OR work order completed)
  const isSLACompleted = status === 'resolved' || workOrderStatus === 'completed';

  // Function to calculate and update the time display
  const updateTimeDisplay = () => {
    if (!shouldShowProgress || !createdAt) return;

    const createdDate = new Date(createdAt);
    
    // ✅ Use resolved timestamp if SLA is completed, otherwise use current time
    const endTime = isSLACompleted && resolvedAt 
      ? new Date(resolvedAt) 
      : new Date();
    
    const elapsedMs = endTime - createdDate;
    const elapsedHours = elapsedMs / (1000 * 60 * 60);

    const tempFixHours = defectTypeSLA.temp_fix_sla_hours || 0;
    const permFixHours = defectTypeSLA.permanent_fix_sla_hours || tempFixHours || 24;
    
    // Use same logic as backend: temp_fix_sla_hours if available, otherwise permanent_fix_sla_hours
    const effectiveSLA = defectTypeSLA.temp_fix_sla_hours || defectTypeSLA.permanent_fix_sla_hours || 24;

    // Display format
    const hours = Math.floor(elapsedHours);
    const minutesDecimal = (elapsedHours - hours) * 60;
    const minutes = Math.floor(minutesDecimal);
    const seconds = Math.floor((minutesDecimal - minutes) * 60);

    // Calculate progress percentage based on effective SLA (to match backend logic)
    const progressPercentage = Math.min((elapsedHours / effectiveSLA) * 100, 100);

    // ✅ Show completion status when SLA is completed
    const displayText = isSLACompleted 
      ? `${hours}h ${minutes}m ${seconds}s (Completed)`
      : `${hours}h ${minutes}m ${seconds}s`;

    setElapsedDisplay({
      text: displayText,
      hours,
      minutes,
      seconds,
      percentage: progressPercentage,
      tempDeadline: `${tempFixHours}h`,
      permDeadline: `${permFixHours}h`,
      effectiveDeadline: `${effectiveSLA}h`,
      isLive: !isSLACompleted,
      isCompleted: isSLACompleted
    });
  };

  // Update time display
  useEffect(() => {
    if (!shouldShowProgress) return;

    // Calculate initial values
    updateTimeDisplay();

    // ✅ Set up interval to update every second only if SLA is still active
    if (!isSLACompleted) {
      const timer = setInterval(() => {
        setCurrentTime(new Date());
        updateTimeDisplay();
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [shouldShowProgress, isSLACompleted, createdAt, defectTypeSLA, resolvedAt]);

  // Return placeholder if no SLA or not validated
  if (!shouldShowProgress) {
    return (
      <Box sx={{
        width: '100%',
        height: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Typography variant="caption" color="text.secondary">
          {!hasSLA ? 'No SLA defined' : 'Pending validation'}
        </Typography>
      </Box>
    );
  }

  const tempFixHours = defectTypeSLA.temp_fix_sla_hours || 0;
  const permFixHours = defectTypeSLA.permanent_fix_sla_hours || tempFixHours || 24;
  const effectiveSLA = defectTypeSLA.temp_fix_sla_hours || defectTypeSLA.permanent_fix_sla_hours || 24;
  const elapsedHours = elapsedDisplay.hours + (elapsedDisplay.minutes / 60) + (elapsedDisplay.seconds / 3600);

  // Calculate progress percentage
  const progressPercentage = elapsedDisplay.percentage;
  const tempFixPercentage = tempFixHours > 0 ? (tempFixHours / permFixHours) * 100 : 0;

  // Determine color and status based on elapsed time (using effective SLA to match backend)
  let progressColor = theme.palette.success.main;
  let progressOpacity = 0.9;
  let overdue = false;
  let critical = false;

  // ✅ Handle completed states (defect resolved OR work order completed)
  if (isSLACompleted) {
    progressColor = theme.palette.success.main;
    progressOpacity = 1.0;
  } else if (elapsedHours >= effectiveSLA) {
    progressColor = theme.palette.error.main;
    overdue = true;
  } else if (tempFixHours > 0 && elapsedHours >= tempFixHours) {
    progressColor = theme.palette.warning.main;
  } else if (tempFixHours > 0 && elapsedHours >= (tempFixHours * 0.7)) {
    progressColor = theme.palette.warning.light;
    critical = true;
  } else if (elapsedHours >= (effectiveSLA * 0.8)) {
    progressColor = theme.palette.warning.light;
  }

  // ✅ Pulsating effect for critical/overdue items (but not when completed)
  const pulseAnimation = (overdue || critical) && !isSLACompleted ? {
    animation: 'pulse 1.5s infinite',
    '@keyframes pulse': {
      '0%': {
        boxShadow: `0 0 0 0 ${alpha(progressColor, 0.7)}`,
      },
      '70%': {
        boxShadow: `0 0 0 6px ${alpha(progressColor, 0)}`,
      },
      '100%': {
        boxShadow: `0 0 0 0 ${alpha(progressColor, 0)}`,
      },
    },
  } : {};

  return (
    <Box sx={{
      width: '100%',
      position: 'relative',
      px: 0.5,
      py: 0.5,
      borderRadius: 1.5,
      transition: 'all 0.2s ease',
      backgroundColor: alpha(theme.palette.background.default, 0.3),
      borderLeft: overdue ? `3px solid ${theme.palette.error.main}` :
                  critical ? `3px solid ${theme.palette.warning.main}` : 'none',
      '&:hover': {
        backgroundColor: alpha(theme.palette.background.default, 0.5),
      }
    }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 0.5,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AccessTimeIcon
            fontSize="small"
            sx={{
              color: overdue ? theme.palette.error.main :
                     critical ? theme.palette.warning.main :
                     theme.palette.text.secondary,
              fontSize: '0.875rem',
              // ✅ Stop animations when SLA is completed
              animation: isSLACompleted 
                ? 'none'
                : overdue
                  ? 'rotateAndBlink 1.5s infinite ease-in-out'
                  : critical
                    ? 'rotate 3s infinite linear'
                    : 'rotate 6s infinite linear',
              '@keyframes rotateAndBlink': {
                '0%': { transform: 'rotate(0deg)', opacity: 1 },
                '25%': { opacity: 0.6 },
                '50%': { transform: 'rotate(180deg)', opacity: 1 },
                '75%': { opacity: 0.6 },
                '100%': { transform: 'rotate(360deg)', opacity: 1 },
              },
              '@keyframes rotate': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 500,
              color: overdue ? theme.palette.error.main :
                     critical ? theme.palette.warning.main :
                     theme.palette.text.secondary,
              fontSize: '0.75rem'
            }}
          >
            {elapsedDisplay.text} / {elapsedDisplay.effectiveDeadline}
          </Typography>
        </Box>

        {/* ✅ Show status chips based on completion and urgency */}
        {isSLACompleted ? (
          <Chip
            label={workOrderStatus === 'completed' ? "Work Completed" : "Resolved"}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              color: theme.palette.success.main,
              backgroundColor: alpha(theme.palette.success.main, 0.1),
              fontWeight: 600,
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
              marginRight: '24px'
            }}
          />
        ) : (overdue || critical) && (
          <Chip
            label={overdue ? "Overdue" : "Urgent"}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              color: overdue ? theme.palette.error.main : theme.palette.warning.main,
              backgroundColor: overdue ? alpha(theme.palette.error.main, 0.1) : alpha(theme.palette.warning.main, 0.1),
              fontWeight: 600,
              border: `1px solid ${alpha(overdue ? theme.palette.error.main : theme.palette.warning.main, 0.2)}`,
              marginRight: '24px',
              ...pulseAnimation
            }}
          />
        )}
      </Box>

      <Box sx={{ position: 'relative', height: 6, width: '100%', mb: 1 }}>
        {/* Background track */}
        <LinearProgress
          variant="determinate"
          value={100}
          sx={{
            height: 6,
            borderRadius: 3,
            backgroundColor: alpha(theme.palette.grey[300], 0.4),
            backdropFilter: 'blur(4px)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: 'transparent',
            }
          }}
        />

        {/* Actual progress */}
        <LinearProgress
          variant="determinate"
          value={progressPercentage}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: 6,
            borderRadius: 3,
            width: '100%',
            backgroundColor: 'transparent',
            '& .MuiLinearProgress-bar': {
              backgroundColor: alpha(progressColor, progressOpacity),
              borderRadius: 3,
              backgroundImage: overdue
                ? `repeating-linear-gradient(45deg, ${alpha(progressColor, 0.9)}, ${alpha(progressColor, 0.9)} 8px, ${alpha(progressColor, 0.65)} 8px, ${alpha(progressColor, 0.65)} 16px)`
                : 'none',
              boxShadow: overdue ? `0 0 6px ${alpha(progressColor, 0.7)}` : 'none',
              transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
              ...pulseAnimation
            }
          }}
        />

        {/* Markers Container */}
        <Box sx={{ position: 'absolute', top: -3, left: 0, width: '100%', height: 12 }}>
          {/* Temporary fix deadline marker */}
          {tempFixHours > 0 && (
            <Tooltip title={`Temporary fix deadline: ${tempFixHours}h`} arrow placement="top">
              <Box
                sx={{
                  position: 'absolute',
                  left: `${tempFixPercentage}%`,
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: 'white',
                  border: `2px solid ${theme.palette.warning.main}`,
                  transform: 'translateX(-50%)',
                  zIndex: 3,
                  transition: 'transform 0.2s ease',
                  '&:hover': {
                    transform: 'translateX(-50%) scale(1.3)',
                  }
                }}
              />
            </Tooltip>
          )}

          {/* Permanent fix deadline marker */}
          <Tooltip title={`Permanent fix deadline: ${permFixHours}h`} arrow placement="top">
            <Box
              sx={{
                position: 'absolute',
                right: 0,
                top: -5,
                zIndex: 3,
                transition: 'transform 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  transform: 'scale(1.3)',
                },
                animation: overdue ? 'shake 1.5s infinite ease-in-out' : 'none',
                '@keyframes shake': {
                  '0%, 100%': { transform: 'rotate(0deg)' },
                  '10%': { transform: 'rotate(-5deg)' },
                  '20%': { transform: 'rotate(5deg)' },
                  '30%': { transform: 'rotate(-5deg)' },
                  '40%': { transform: 'rotate(5deg)' },
                  '50%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(0deg)' },
                }
              }}
            >
              <BombIcon
                sx={{
                  fontSize: 16,
                  color: theme.palette.error.main
                }}
              />
            </Box>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

export default TimeProgressBar; 